import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Font,
  pdf,
} from "@react-pdf/renderer";
import { api } from "@shared/routes";

export async function emitReclassificationPdf(laudoId: string, token: string | undefined) {
  const response = await fetch(
    api.remontaReclassificacao.emit.path.replace(":id", laudoId),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({}),
    },
  );
  const body = await response.json().catch(() => ({ message: "Erro ao emitir PDF." }));
  if (!response.ok) throw new Error(body.message ?? "Erro ao emitir PDF.");
  return body as { pdfUrl: string };
}

// ── Font registration ──────────────────────────────────────────────────────
Font.register({
  family: "Inter",
  fonts: [
    {
      src: "https://cdn.jsdelivr.net/npm/@fontsource/inter@5/files/inter-latin-400-normal.woff",
      fontWeight: 400,
    },
    {
      src: "https://cdn.jsdelivr.net/npm/@fontsource/inter@5/files/inter-latin-700-normal.woff",
      fontWeight: 700,
    },
  ],
});

// ── Color theme ────────────────────────────────────────────────────────────
const C = {
  primary: "#1E3A5F",
  accent: "#2563EB",
  success: "#059669",
  danger: "#DC2626",
  warning: "#D97706",
  textDark: "#1A1A2E",
  textMid: "#4A5568",
  textLight: "#718096",
  bgLight: "#F7F8FA",
  borderGray: "#E2E8F0",
  white: "#FFFFFF",
} as const;

// ── Element status labels ──────────────────────────────────────────────────
const ELEMENTO_STATUS_LABELS: Record<string, string> = {
  integro: "Íntegro",
  danificado: "Danificado",
  substituido: "Substituído",
  reparado: "Reparado",
  inexistente: "Inexistente",
  nao_avaliado: "Não avaliado",
};

// ── Types ──────────────────────────────────────────────────────────────────
export interface LaudoRemontaItemPdf {
  id: string;
  elementoId: string;
  status: string;
  observacao?: string | null;
  fotoUrl?: string | null;
  elemento?: {
    nome: string;
    descricao?: string | null;
    categoriaId: string;
    categoria?: { nome: string } | null;
  } | null;
}

export interface LaudoRemontaFotoPdf {
  id: string;
  storageUrl: string;
  elementoNome?: string | null;
}

export interface LaudoRemontaWithDetails {
  id: string;
  status: string;
  conclusao?: string | null;
  justificativa?: string | null;
  criadoEm: string | Date;
  veiculo?: {
    placa: string;
    chassi?: string | null;
    marca: string;
    modelo: string;
    anoFabricacao?: number | null;
    tipoVeiculo: string;
    cor?: string | null;
  } | null;
  rt?: {
    nome: string;
    crea?: string | null;
  } | null;
  art?: {
    numeroArt: string;
  } | null;
  itens?: LaudoRemontaItemPdf[];
  fotos?: LaudoRemontaFotoPdf[];
}

// ── StyleSheet ─────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: "Inter",
    fontSize: 10,
    paddingLeft: 32,
    paddingRight: 32,
    paddingTop: 28,
    paddingBottom: 28,
    backgroundColor: C.white,
    color: C.textDark,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: C.primary,
  },
  headerTitle: { fontSize: 18, fontWeight: 700, color: C.primary, flex: 1 },
  headerSubtitle: { fontSize: 10, color: C.textMid },
  section: { marginBottom: 14 },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: C.primary,
    marginBottom: 6,
    paddingBottom: 3,
    borderBottomWidth: 1,
    borderBottomColor: C.borderGray,
  },
  infoRow: { flexDirection: "row", marginBottom: 3 },
  infoLabel: { fontSize: 9, color: C.textLight, width: 90 },
  infoValue: { fontSize: 9, color: C.textDark, flex: 1 },
  categoryHeader: {
    backgroundColor: C.bgLight,
    padding: 5,
    marginBottom: 4,
    marginTop: 6,
  },
  categoryTitle: { fontSize: 10, fontWeight: 700, color: C.primary },
  elementRow: {
    flexDirection: "row",
    paddingVertical: 3,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: C.borderGray,
  },
  elementName: { fontSize: 9, color: C.textDark, flex: 2 },
  elementStatus: { fontSize: 9, fontWeight: 700, flex: 1 },
  elementObs: { fontSize: 8, color: C.textMid, flex: 2 },
  photoGrid: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 6 },
  photoThumb: {
    width: 120,
    height: 90,
    objectFit: "cover",
    borderWidth: 0.5,
    borderColor: C.borderGray,
  },
  photoBlock: { marginTop: 6, marginBottom: 4 },
  photoLabel: {
    fontSize: 8,
    fontWeight: 700,
    color: C.textMid,
    marginBottom: 3,
  },
  photoImg: {
    width: 160,
    height: 120,
    objectFit: "cover",
    borderWidth: 0.5,
    borderColor: C.borderGray,
  },
  artBox: {
    backgroundColor: "#EFF6FF",
    borderWidth: 1,
    borderColor: C.accent,
    borderRadius: 4,
    padding: 8,
    marginTop: 8,
    flexDirection: "row",
    alignItems: "center",
  },
  artBoxBar: {
    width: 4,
    height: 32,
    backgroundColor: C.accent,
    marginRight: 10,
    borderRadius: 2,
  },
  artBoxLabel: { fontSize: 7, fontWeight: 700, color: C.accent, marginBottom: 2 },
  artBoxValue: { fontSize: 13, fontWeight: 700, color: C.textDark },
  conclusaoBlock: { backgroundColor: C.bgLight, padding: 10, marginTop: 6 },
  conclusaoLabel: { fontSize: 11, fontWeight: 700 },
  conclusaoJustificativa: { fontSize: 9, color: C.textMid, marginTop: 4 },
  footer: {
    position: "absolute",
    bottom: 16,
    left: 32,
    right: 32,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 0.5,
    borderTopColor: C.borderGray,
    paddingTop: 4,
  },
  footerText: { fontSize: 8, color: C.textLight },
});

// ── Helpers ────────────────────────────────────────────────────────────────
function getStatusColor(status: string): string {
  switch (status) {
    case "integro": return C.success;
    case "danificado": return C.danger;
    case "substituido": return C.accent;
    case "reparado": return C.warning;
    default: return C.textLight;
  }
}

function formatDate(d: string | Date): string {
  return new Date(d).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ── Main component ─────────────────────────────────────────────────────────
export function LaudoRemontaPdf({
  laudo,
}: {
  laudo: LaudoRemontaWithDetails;
}): React.ReactElement {
  const { veiculo, rt, art, itens = [], fotos = [] } = laudo;

  // Group itens by categoria
  const byCategoria = new Map<string, { nome: string; items: LaudoRemontaItemPdf[] }>();
  for (const item of itens) {
    const catId = item.elemento?.categoriaId ?? "sem-categoria";
    const catNome = item.elemento?.categoria?.nome ?? "Sem categoria";
    if (!byCategoria.has(catId)) byCategoria.set(catId, { nome: catNome, items: [] });
    byCategoria.get(catId)!.items.push(item);
  }

  return (
    <Document
      title={`Laudo de Recuperabilidade — ${veiculo?.placa ?? "sem-placa"}`}
      author="ReMonta"
    >
      <Page size="A4" style={s.page}>
        {/* Header */}
        <View style={s.headerRow} fixed>
          <Text style={s.headerTitle}>Laudo de Recuperabilidade</Text>
          <View>
            <Text style={s.headerSubtitle}>ReMonta · CONTRAN nº 810/2020</Text>
            <Text style={s.headerSubtitle}>
              Laudo #{laudo.id.slice(0, 8)} · {formatDate(laudo.criadoEm)}
            </Text>
          </View>
        </View>

        {/* Dados do Veículo */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>Dados do Veículo</Text>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>Placa</Text>
            <Text style={s.infoValue}>{veiculo?.placa ?? "—"}</Text>
          </View>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>Chassi</Text>
            <Text style={s.infoValue}>{veiculo?.chassi ?? "—"}</Text>
          </View>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>Marca / Modelo</Text>
            <Text style={s.infoValue}>
              {veiculo?.marca} {veiculo?.modelo} · {veiculo?.anoFabricacao ?? "—"}
            </Text>
          </View>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>Tipo</Text>
            <Text style={s.infoValue}>{veiculo?.tipoVeiculo ?? "—"}</Text>
          </View>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>Cor</Text>
            <Text style={s.infoValue}>{veiculo?.cor ?? "—"}</Text>
          </View>
        </View>

        {/* Responsável Técnico */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>Responsável Técnico</Text>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>Nome</Text>
            <Text style={s.infoValue}>{rt?.nome ?? "—"}</Text>
          </View>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>CREA</Text>
            <Text style={s.infoValue}>{rt?.crea ?? "—"}</Text>
          </View>
          {/* ART em destaque */}
          <View style={s.artBox}>
            <View style={s.artBoxBar} />
            <View>
              <Text style={s.artBoxLabel}>ANOTAÇÃO DE RESPONSABILIDADE TÉCNICA (ART)</Text>
              <Text style={s.artBoxValue}>{art?.numeroArt ?? "Não informado"}</Text>
            </View>
          </View>
        </View>

        {/* Elementos Estruturais */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>Elementos Estruturais Avaliados</Text>
          {Array.from(byCategoria.entries()).map(([catId, { nome, items }]) => (
            <View key={catId}>
              <View style={s.categoryHeader} wrap={false}>
                <Text style={s.categoryTitle}>{nome}</Text>
              </View>
              {items.map((item) => (
                <View key={item.id} wrap={false}>
                  <View style={s.elementRow}>
                    <Text style={s.elementName}>
                      {item.elemento?.nome ?? item.elementoId}
                    </Text>
                    <Text
                      style={[s.elementStatus, { color: getStatusColor(item.status) }]}
                    >
                      {ELEMENTO_STATUS_LABELS[item.status] ?? item.status}
                    </Text>
                    <Text style={s.elementObs}>{item.observacao ?? ""}</Text>
                  </View>
                  {item.fotoUrl && (
                    <View style={s.photoBlock}>
                      <Text style={s.photoLabel}>
                        Foto — {item.elemento?.nome ?? item.elementoId}
                      </Text>
                      <Image src={item.fotoUrl} style={s.photoImg} />
                    </View>
                  )}
                </View>
              ))}
            </View>
          ))}
          {itens.length === 0 && (
            <Text style={{ fontSize: 9, color: C.textLight, marginTop: 4 }}>
              Nenhum elemento avaliado.
            </Text>
          )}
        </View>

        {/* Registro Fotográfico */}
        {fotos.length > 0 && (
          <View style={s.section} break>
            <Text style={s.sectionTitle}>Registro Fotográfico</Text>
            <View style={s.photoGrid}>
              {fotos.map((foto) => (
                <View key={foto.id} style={s.photoBlock}>
                  {foto.elementoNome && (
                    <Text style={s.photoLabel}>{foto.elementoNome}</Text>
                  )}
                  <Image src={foto.storageUrl} style={s.photoThumb} />
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Conclusão Técnica */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>Conclusão Técnica</Text>
          {laudo.conclusao ? (
            <View style={s.conclusaoBlock}>
              <Text
                style={[
                  s.conclusaoLabel,
                  {
                    color:
                      laudo.conclusao === "recuperavel" ? C.success : C.danger,
                  },
                ]}
              >
                {laudo.conclusao === "recuperavel" ? "RECUPERÁVEL" : "IRRECUPERÁVEL"}
              </Text>
              <Text style={s.conclusaoJustificativa}>
                {laudo.justificativa ?? "—"}
              </Text>
            </View>
          ) : (
            <Text style={{ fontSize: 9, color: C.textLight }}>
              Conclusão não definida.
            </Text>
          )}
        </View>

        {/* Footer */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>
            ReMonta — Sistema de Reclassificação de Monta · CONTRAN nº 810/2020
          </Text>
          <Text
            style={s.footerText}
            render={({ pageNumber, totalPages }) =>
              `Página ${pageNumber} de ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}

export { pdf };
